/**
 * Strategy Detail drill-down (spec Part 21): Strategy Details -> Legs ->
 * Orders -> P&L -> Greeks -> Risk -> Modify -> Exit, all in one dialog so
 * the trader reaches every one of those from a single click on an Active
 * Strategy row (spec Part 29's "minimal clicks" instruction) — no separate
 * page per section.
 *
 * Read-only itself: opening/refreshing only calls
 * `positionService.getStrategyPosition`/`listOrders`. Modify/Exit are
 * delegated entirely to `ModifyOrderDialog`/`ExitDialog`, which are the
 * only components here that can affect a real broker order.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import positionService from "@/services/positionService";
import type { ExitResultRead, OrderMonitorRead, StrategyPositionRead } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { BROKER_DISPLAY_LABELS, fmtCurrency, fmtNum } from "@/pages/strategy-adjustment/constants";
import { ORDER_STATE_COLORS, ORDER_STATE_LABELS } from "@/pages/execution/constants";
import { ORDER_STATE_ICONS, RECONCILIATION_COLORS, RECONCILIATION_LABELS, STRATEGY_POSITION_STATUS_COLORS } from "./constants";
import ModifyOrderDialog from "./ModifyOrderDialog";
import ExitDialog from "./ExitDialog";

export function StrategyDetailDialog({
  executionId,
  open,
  onClose,
}: {
  executionId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const [strategy, setStrategy] = useState<StrategyPositionRead | null>(null);
  const [orders, setOrders] = useState<OrderMonitorRead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exitOpen, setExitOpen] = useState(false);
  const [exitResult, setExitResult] = useState<ExitResultRead | null>(null);
  const [modifyOrder, setModifyOrder] = useState<OrderMonitorRead | null>(null);

  const load = useCallback(async () => {
    if (!executionId) return;
    setLoading(true);
    setError(null);
    // Clear whatever the PREVIOUS executionId loaded before fetching the
    // new one -- otherwise a 404 (or any other failure) below leaves the
    // error banner shown alongside stale data from a totally different
    // strategy, since only `error` used to be reset here, never `strategy`/
    // `orders` themselves.
    setStrategy(null);
    setOrders([]);
    try {
      const [strategyResult, orderList] = await Promise.all([
        positionService.getStrategyPosition(executionId),
        positionService.listOrders(),
      ]);
      setStrategy(strategyResult);
      setOrders(orderList.orders.filter((o) => o.execution_id === executionId));
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [executionId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const orderForLeg = (leg: StrategyPositionRead["legs"][number]) =>
    orders.find((o) => o.strike === leg.strike && o.option_type === leg.option_type && o.side === leg.side);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {strategy ? `${strategy.strategy_key} — ${strategy.underlying}` : "Strategy Detail"}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {loading && !strategy && <Typography variant="body2">Loading strategy detail…</Typography>}
          {exitResult && (
            <Alert severity={exitResult.position_verified_closed ? "success" : "warning"} sx={{ mb: 2 }}>
              Exit result: {exitResult.status.replace(/_/g, " ")}
              {exitResult.gross_pnl != null && ` — Gross P&L ${fmtCurrency(exitResult.gross_pnl)}`}
            </Alert>
          )}

          {strategy && (
            <Stack spacing={3}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip label={BROKER_DISPLAY_LABELS[strategy.broker]} size="small" />
                <Chip
                  label={`Strategy Status: ${strategy.status.replace(/_/g, " ")}`}
                  color={STRATEGY_POSITION_STATUS_COLORS[strategy.status] ?? "default"}
                  size="small"
                />
                {strategy.broker_connection_lost && (
                  <Chip label="⚠ BROKER CONNECTION LOST" color="error" size="small" />
                )}
              </Stack>

              {strategy.broker_connection_lost && (
                <Alert severity="warning">
                  Position status could not be verified with the broker.{" "}
                  <Button size="small" href="/broker-accounts">
                    Reconnect Broker
                  </Button>
                </Alert>
              )}
              {strategy.mismatch && (
                <Alert severity="error">
                  ⚠ POSITION MISMATCH — Expected: {strategy.mismatch.expected_legs} legs, Broker:{" "}
                  {strategy.mismatch.broker_legs} legs. Status: REQUIRES ATTENTION. TekInvest never places a
                  corrective order automatically — review the legs below and act manually if needed.
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Entry Credit</Typography>
                  <Typography variant="body1">{fmtCurrency(strategy.entry_credit)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Current Value</Typography>
                  <Typography variant="body1">{fmtCurrency(strategy.current_value)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Current P&L</Typography>
                  <Typography variant="body1" color={(strategy.current_pnl ?? 0) >= 0 ? "success.main" : "error.main"}>
                    {fmtCurrency(strategy.current_pnl)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Realized P&L</Typography>
                  <Typography variant="body1">{fmtCurrency(strategy.realized_pnl)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Probability of Profit</Typography>
                  <Typography variant="body1">{strategy.probability_of_profit != null ? `${fmtNum(strategy.probability_of_profit, 1)}%` : "—"}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Maximum Risk</Typography>
                  <Typography variant="body1">{fmtCurrency(strategy.maximum_risk)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Remaining Risk</Typography>
                  <Typography variant="body1">{fmtCurrency(strategy.remaining_risk)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Days to Expiry</Typography>
                  <Typography variant="body1">{strategy.days_to_expiry ?? "—"}</Typography>
                </Grid>
              </Grid>

              {strategy.greeks ? (
                <Stack direction="row" spacing={3}>
                  <Typography variant="body2">Delta: {fmtNum(strategy.greeks.delta, 4)}</Typography>
                  <Typography variant="body2">Gamma: {fmtNum(strategy.greeks.gamma, 6)}</Typography>
                  <Typography variant="body2">Theta: {fmtNum(strategy.greeks.theta, 4)}</Typography>
                  <Typography variant="body2">Vega: {fmtNum(strategy.greeks.vega, 4)}</Typography>
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Current Greeks unavailable — no live spot could be resolved for this underlying right now.
                </Typography>
              )}

              <Divider />
              <Typography variant="subtitle2">Legs / Positions / Orders</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Side</TableCell>
                    <TableCell>Strike</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Qty (Orig/Exited/Open)</TableCell>
                    <TableCell>Order Status</TableCell>
                    <TableCell align="right">Avg Price</TableCell>
                    <TableCell align="right">LTP</TableCell>
                    <TableCell align="right">Net P&L</TableCell>
                    <TableCell>Reconciliation</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {strategy.legs.map((leg) => {
                    const order = orderForLeg(leg);
                    return (
                      <TableRow key={leg.position_id}>
                        <TableCell>{leg.side}</TableCell>
                        <TableCell>{String(leg.strike)}</TableCell>
                        <TableCell>{leg.option_type}</TableCell>
                        <TableCell align="right">
                          {leg.original_quantity} / {leg.exited_quantity} / {leg.remaining_quantity}
                        </TableCell>
                        <TableCell>
                          {order && (
                            <Chip
                              size="small"
                              label={`${ORDER_STATE_ICONS[order.status]} ${ORDER_STATE_LABELS[order.status]}`}
                              color={ORDER_STATE_COLORS[order.status]}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">{fmtCurrency(leg.average_price)}</TableCell>
                        <TableCell align="right">{fmtCurrency(leg.last_price)}</TableCell>
                        <TableCell align="right">{fmtCurrency(leg.net_pnl)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={RECONCILIATION_LABELS[leg.reconciliation_status]} color={RECONCILIATION_COLORS[leg.reconciliation_status]} />
                        </TableCell>
                        <TableCell align="right">
                          {order?.modifiable && (
                            <Button size="small" onClick={() => setModifyOrder(order)}>
                              Modify
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          <Button size="small" onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!strategy || strategy.legs.every((l) => l.remaining_quantity <= 0)}
            onClick={() => setExitOpen(true)}
          >
            Exit Strategy
          </Button>
        </DialogActions>
      </Dialog>

      <ModifyOrderDialog
        order={modifyOrder}
        open={Boolean(modifyOrder)}
        onClose={() => setModifyOrder(null)}
        onModified={load}
      />
      <ExitDialog
        executionId={executionId}
        open={exitOpen}
        onClose={() => setExitOpen(false)}
        onExited={(result) => {
          setExitResult(result);
          load();
        }}
      />
    </>
  );
}

export default StrategyDetailDialog;
